export type AudioKind = "mic" | "system";

export interface AudioLevels {
  mic: number;
  system: number;
}

/**
 * Mixes microphone and system audio into ONE track for the encoder, with a
 * live level meter and a mute that is real silence (gain 0), never a stopped
 * track — so the audio track stays continuous and in sync with the video.
 */
export class AudioMixer {
  private ctx: AudioContext | null = null;
  private dest: MediaStreamAudioDestinationNode | null = null;
  private gains = new Map<AudioKind, GainNode>();
  private analysers = new Map<AudioKind, AnalyserNode>();
  private inputs = new Map<AudioKind, MediaStreamAudioSourceNode>();
  private streams = new Map<AudioKind, MediaStream>();

  /** The stream currently feeding a kind, so callers can tell whether it moved. */
  streamFor(kind: AudioKind): MediaStream | null {
    return this.streams.get(kind) ?? null;
  }
  private muted = new Map<AudioKind, boolean>([
    ["mic", false],
    ["system", false],
  ]);
  private sampleBuffer = new Float32Array(512);
  private closed = false;

  get isReady(): boolean {
    return this.ctx !== null && this.dest !== null;
  }

  has(kind: AudioKind): boolean {
    return this.inputs.has(kind);
  }

  private async ensure(): Promise<void> {
    if (this.closed) throw new Error("audio mixer is closed");
    if (this.ctx && this.dest) {
      if (this.ctx.state === "suspended") await this.ctx.resume();
      return;
    }
    const ctx = new AudioContext({ sampleRate: 48_000, latencyHint: "interactive" });
    const dest = ctx.createMediaStreamDestination();
    this.ctx = ctx;
    this.dest = dest;
    for (const kind of ["mic", "system"] as const) {
      const gain = ctx.createGain();
      gain.gain.value = this.muted.get(kind) ? 0 : 1;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      gain.connect(analyser);
      gain.connect(dest);
      this.gains.set(kind, gain);
      this.analysers.set(kind, analyser);
    }
    if (ctx.state === "suspended") await ctx.resume();
  }

  async attach(kind: AudioKind, stream: MediaStream): Promise<void> {
    await this.ensure();
    const ctx = this.ctx;
    const gain = this.gains.get(kind);
    if (!ctx || !gain) throw new Error("audio graph not ready");
    this.detach(kind);
    const audioTrack = stream.getAudioTracks()[0];
    if (!audioTrack) throw new Error(`no audio track in ${kind} stream`);
    const source = ctx.createMediaStreamSource(new MediaStream([audioTrack]));
    source.connect(gain);
    this.inputs.set(kind, source);
    this.streams.set(kind, stream);
    // A device that disappears must not silently kill the recording.
    audioTrack.addEventListener("ended", () => this.detach(kind), { once: true });
  }

  detach(kind: AudioKind): void {
    const source = this.inputs.get(kind);
    if (source) {
      try {
        source.disconnect();
      } catch {
        // Already disconnected.
      }
    }
    this.inputs.delete(kind);
    this.streams.delete(kind);
  }

  setMuted(kind: AudioKind, muted: boolean): void {
    this.muted.set(kind, muted);
    const gain = this.gains.get(kind);
    const ctx = this.ctx;
    if (gain && ctx) {
      gain.gain.setTargetAtTime(muted ? 0 : 1, ctx.currentTime, 0.01);
    }
  }

  isMuted(kind: AudioKind): boolean {
    return this.muted.get(kind) ?? false;
  }

  /**
   * The destination track for the encoder, creating the graph if needed. A
   * take that starts silent must still carry an audio track, or turning the
   * microphone on mid-take would have nowhere to go.
   */
  async ensureOutput(): Promise<MediaStream> {
    await this.ensure();
    return this.outputStream() ?? new MediaStream();
  }

  /** The single mixed track for the encoder, or null when nothing is on. */
  outputStream(): MediaStream | null {
    if (!this.dest) return null;
    const track = this.dest.stream.getAudioTracks()[0];
    if (!track) return null;
    return new MediaStream([track]);
  }

  levels(): AudioLevels {
    return {
      mic: this.level("mic"),
      system: this.level("system"),
    };
  }

  private level(kind: AudioKind): number {
    const analyser = this.analysers.get(kind);
    if (!analyser || !this.inputs.has(kind) || this.muted.get(kind)) return 0;
    analyser.getFloatTimeDomainData(this.sampleBuffer);
    let sum = 0;
    for (const sample of this.sampleBuffer) sum += sample * sample;
    const rms = Math.sqrt(sum / this.sampleBuffer.length);
    return Math.min(1, rms * 6);
  }

  async close(): Promise<void> {
    this.closed = true;
    for (const kind of ["mic", "system"] as const) this.detach(kind);
    const ctx = this.ctx;
    this.ctx = null;
    this.dest = null;
    this.gains.clear();
    this.analysers.clear();
    if (ctx && ctx.state !== "closed") {
      try {
        await ctx.close();
      } catch {
        // Already closed.
      }
    }
  }
}
